import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import config from '../../config';
import { Phone, CheckCircle, Clock, Search, Flag } from 'lucide-react';
import { useSortedPage, SortTh, PageBar } from '../../components/admin/DataTable';
import { formatDateTimeDMY } from '../../lib/dates';

function StatusBadge({ status }) {
  if (status === 'follow_up') {
    return (
      <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 rounded-full px-2.5 py-0.5 text-xs font-semibold">
        <Flag className="w-3 h-3" /> Follow-up
      </span>
    );
  }
  if (status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 bg-[#f37721]/15 text-[#f37721] rounded-full px-2.5 py-0.5 text-xs font-semibold">
        <Clock className="w-3 h-3" /> Pending
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 rounded-full px-2.5 py-0.5 text-xs font-semibold">
      <CheckCircle className="w-3 h-3" /> Contacted
    </span>
  );
}

function actionStamp(c) {
  if (c.status === 'follow_up') return c.follow_up_at || c.contacted_at || c.created_at;
  if (c.status === 'contacted') return c.contacted_at || c.created_at;
  return c.contacted_at || c.follow_up_at || c.created_at;
}

export default function Callbacks() {
  const { getAuthHeaders } = useAuth();
  const [callbacks, setCallbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState("");

  const fetchCallbacks = useCallback(async () => {
    try {
      const { data } = await axios.get(`${config.API}/admin/callbacks`, { headers: getAuthHeaders() });
      setCallbacks(data.callbacks);
    } catch (err) { if (process.env.NODE_ENV === 'development') console.error(err); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchCallbacks(); }, [fetchCallbacks]);

  const patchCallback = async (id, body) => {
    setSavingId(id);
    try {
      await axios.patch(`${config.API}/admin/callbacks/${id}`, body, { headers: getAuthHeaders() });
      await fetchCallbacks();
    } catch (err) { if (process.env.NODE_ENV === 'development') console.error(err); }
    finally { setSavingId(""); }
  };

  const filtered = callbacks.filter(c => {
    if (filter === 'open') return c.status === 'pending' || c.status === 'follow_up';
    if (filter && c.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return c.name?.toLowerCase().includes(q) || c.phone?.includes(q) || c.vaccine_interest?.toLowerCase().includes(q) || (c.remarks || "").toLowerCase().includes(q);
    }
    return true;
  });

  const pendingCount = callbacks.filter(c => c.status === 'pending').length;
  const followCount = callbacks.filter(c => c.status === 'follow_up').length;
  const table = useSortedPage(filtered, { defaultKey: 'created_at', defaultDir: 'desc', pageSize: 20 });

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#673c96] border-t-transparent" /></div>;

  return (
    <div data-testid="admin-callbacks" className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <Phone className="w-5 h-5 text-[#673c96]" />
          <h2 className="text-lg font-bold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>
            Callback Requests ({callbacks.length})
          </h2>
          {pendingCount > 0 && (
            <span className="bg-[#f37721] text-white text-xs font-bold rounded-full px-2.5 py-0.5">{pendingCount} pending</span>
          )}
          {followCount > 0 && (
            <span className="bg-amber-500 text-white text-xs font-bold rounded-full px-2.5 py-0.5">{followCount} follow-up</span>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#673c96]" />
          <input data-testid="callback-search" value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[#673c96]/20 text-sm focus:border-[#673c96] focus:ring-1 focus:ring-[#673c96]/20 outline-none"
            placeholder="Search by name, phone, vaccine, or remarks..." />
        </div>
        <select data-testid="callback-filter" value={filter} onChange={e => setFilter(e.target.value)}
          className="px-4 py-2.5 rounded-lg border border-[#673c96]/20 text-sm bg-white focus:border-[#673c96] outline-none">
          <option value="open">Open (pending + follow-up)</option>
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="follow_up">Follow-up</option>
          <option value="contacted">Contacted</option>
        </select>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-[#673c96]/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="callbacks-table">
            <thead className="bg-[#fdfcff]">
              <tr className="border-b border-[#673c96]/10">
                <SortTh label="Name" k="name" sortKey={table.sortKey} sortDir={table.sortDir} onSort={table.toggleSort} />
                <SortTh label="Phone" k="phone" sortKey={table.sortKey} sortDir={table.sortDir} onSort={table.toggleSort} />
                <SortTh label="Vaccine interest" k="vaccine_interest" sortKey={table.sortKey} sortDir={table.sortDir} onSort={table.toggleSort} />
                <SortTh label="Status" k="status" sortKey={table.sortKey} sortDir={table.sortDir} onSort={table.toggleSort} />
                <SortTh label="Requested at" k="created_at" sortKey={table.sortKey} sortDir={table.sortDir} onSort={table.toggleSort} />
                <th className="text-left py-3 px-4 text-xs font-semibold text-[#673c96]">Action</th>
              </tr>
            </thead>
            <tbody>
              {table.slice.map(c => {
                const remarks = drafts[c.id] !== undefined ? drafts[c.id] : (c.remarks || "");
                return (
                <tr key={c.id} className={`border-b border-[#673c96]/5 hover:bg-[#fdfcff] transition-colors ${c.status === 'pending' ? 'bg-[#f37721]/[0.02]' : c.status === 'follow_up' ? 'bg-amber-50/60' : ''}`}
                  data-testid={`callback-row-${c.id}`}>
                  <td className="py-3 px-4">
                    <p className="font-medium text-[#311b52]">{c.name}</p>
                    {c.is_registered ? (
                      <span className="inline-flex mt-1 bg-emerald-50 text-emerald-800 rounded-full px-2 py-0.5 text-[10px] font-semibold" data-testid={`callback-registered-${c.id}`}>
                        Registered customer{c.registered_customer_name ? ` · ${c.registered_customer_name}` : ''}
                      </span>
                    ) : (
                      <span className="inline-flex mt-1 bg-[#f5f3f7] text-[#673c96] rounded-full px-2 py-0.5 text-[10px] font-semibold">Callback lead</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <a href={`tel:${c.phone}`} className="text-[#673c96] hover:text-[#f37721] transition-colors">{c.phone}</a>
                  </td>
                  <td className="py-3 px-4 text-[#3e195e]">{c.vaccine_interest || '—'}</td>
                  <td className="py-3 px-4">
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="py-3 px-4 text-xs text-[#673c96]">
                    {formatDateTimeDMY(c.created_at)}
                  </td>
                  <td className="py-3 px-4 min-w-[220px]">
                    <p className="text-xs text-[#673c96] mb-1.5" data-testid={`callback-action-at-${c.id}`}>
                      {actionStamp(c) ? formatDateTimeDMY(actionStamp(c)) : "—"}
                    </p>
                    <textarea
                      rows={2}
                      value={remarks}
                      placeholder="Remarks for later follow-up…"
                      data-testid={`callback-remarks-${c.id}`}
                      onChange={(e) => setDrafts((d) => ({ ...d, [c.id]: e.target.value }))}
                      className="w-full rounded-lg border border-[#673c96]/20 px-2 py-1.5 text-xs mb-1.5"
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      <button type="button" disabled={savingId === c.id}
                        onClick={() => patchCallback(c.id, { remarks })}
                        className="text-xs font-medium text-[#673c96] hover:text-[#f37721]">
                        Save remarks
                      </button>
                      <a href={`tel:${c.phone}`} data-testid={`call-btn-${c.id}`}
                        className="inline-flex items-center gap-1 text-xs font-medium text-[#673c96] hover:text-[#f37721] transition-colors">
                        <Phone className="w-3 h-3" /> Call
                      </a>
                      {c.status !== 'contacted' && (
                        <button type="button" onClick={() => patchCallback(c.id, { status: 'contacted', remarks })} data-testid={`mark-contacted-${c.id}`}
                          className="inline-flex items-center gap-1 text-xs font-medium text-green-600 hover:text-green-800 transition-colors">
                          <CheckCircle className="w-3 h-3" /> Mark Done
                        </button>
                      )}
                      {c.status !== 'follow_up' && (
                        <button type="button" onClick={() => patchCallback(c.id, { status: 'follow_up', remarks })} data-testid={`mark-followup-${c.id}`}
                          className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-900 transition-colors">
                          <Flag className="w-3 h-3" /> Follow-Up
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-sm text-[#673c96]">No callback requests</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <PageBar page={table.page} pages={table.pages} total={table.total} setPage={table.setPage} noun="callbacks" />
      </div>
    </div>
  );
}
